<template>
  <section class="card" @click="onPageSurfaceClick">
    <div class="page-head">
      <button
        v-if="showBackButton"
        class="back-btn"
        type="button"
        title="返回"
        @click="goBack"
        @mouseenter="backBtnHover = true"
        @mouseleave="backBtnHover = false"
      >
        <img
          class="back-btn-icon"
          :src="backBtnHover ? backAboutActiveIcon : backAboutIcon"
          alt="返回"
        >
      </button>
      <h2>{{ pageTitle }}</h2>
    </div>
    <p class="tip">{{ tipText }}</p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="filters filters-panel">
      <template v-if="!useMembershipApplyMode && !isFormLocked">
        <div class="flt">
        <FancySelect
          v-model="formId"
            class="flt-select flt-select--wide"
          :options="formSelectOptions"
          :searchable="true"
            placeholder="表单"
            @change="onFilterSelectChange"
        />
        </div>
      </template>
      <div class="flt">
      <FancySelect
        v-model="reviewFilter"
          class="flt-select flt-select--status"
        :options="reviewStatusOptions"
          placeholder="状态"
          @change="onFilterSelectChange"
        />
      </div>
      <div class="flt">
        <input
          v-model.trim="applicantNameQ"
          class="flt-input flt-input--wide"
          placeholder="申请人 / 会员代表"
          aria-label="申请人或会员代表"
          @keyup.enter="onSearch"
        />
      </div>
      <div class="flt">
        <input
          v-model.trim="applicantUnitQ"
          class="flt-input"
          placeholder="申请单位"
          aria-label="申请单位"
          @keyup.enter="onSearch"
        />
      </div>
      <div class="flt flt-range">
        <input v-model="startTimeQ" class="flt-input flt-input--date" type="date" aria-label="开始日期" />
        <span class="flt-sep">至</span>
        <input v-model="endTimeQ" class="flt-input flt-input--date" type="date" aria-label="结束日期" />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" :disabled="queryDisabled" @click="onSearch">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" :disabled="queryDisabled" @click="onResetFilters">重置</button>
      <button
        type="button"
          class="btn btn-edit filter-action-btn"
        :disabled="!displayRows.length || loading"
        @click="batchExportWord"
      >
        批量导出（本页）
      </button>
    </div>
      <span v-if="useMembershipApplyMode" class="filters-break" aria-hidden="true"></span>
      <template v-if="useMembershipApplyMode">
        <div class="flt">
        <FancySelect
          v-model="batchConfirmTemplateId"
            class="flt-select flt-select--template"
          :options="confirmTemplateSelectOptions"
            placeholder="确认函模板"
        />
        </div>
        <button
          type="button"
          class="btn btn-primary filter-action-btn"
          :disabled="!batchConfirmTemplateId || !batchApproveSelectedRows.length"
          @click="openBatchApproveDialog"
        >
          批量表审通过并发确认函
        </button>
        <button type="button" class="btn btn-edit filter-action-btn" @click="goConfirmTemplateManage">确认函模板</button>
      </template>
      </div>
    <p v-if="useMembershipApplyMode" class="filters-hint">
      表审通过时下发确认函，并生成「入会首次会费」订单（会费在「我的订单」缴纳，不再发放缴费函）。
    </p>

    <div v-if="loading" class="muted">加载中…</div>
    <div v-else-if="canRenderRows && displayRows.length" class="table-scroll">
      <table class="submission-table">
      <thead>
        <tr>
            <th v-if="useMembershipApplyMode" class="col-check">
            <input type="checkbox" :checked="allApproveSelectableChecked" @change="toggleCheckAllApproveSelectable" />
          </th>
            <th class="col-seq">序号</th>
            <th class="col-name">申请人</th>
            <th class="col-name">会员代表</th>
            <th class="col-unit">申请单位</th>
            <th class="col-flow">流程状态</th>
            <th class="col-audit">审核信息</th>
            <th class="col-time">提交时间</th>
            <th class="col-sticky-right col-ops">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(r, idx) in displayRows"
          :key="r.id"
          :ref="`submissionRow-${r.id}`"
          :class="{ 'row-highlight': Number(highlightedSubmissionId) === Number(r.id) }"
        >
            <td v-if="useMembershipApplyMode" class="col-check">
            <input
              type="checkbox"
              :disabled="!canApproveApply(r)"
              :checked="selectedSubmissionIds.includes(r.id)"
              @change="toggleSubmissionChecked(r.id, r)"
            />
          </td>
            <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
            <td class="col-name">{{ r.submitterName || r.applicantName || '—' }}</td>
            <td class="col-name">{{ r.representativeName || '—' }}</td>
            <td class="col-unit">
              <span class="cell-ellipsis" :title="r.applicantUnit || ''">{{ r.applicantUnit || '—' }}</span>
            </td>
            <td class="col-flow">
            <span class="pill" :class="reviewPillClass(pipelineStatusText(r))">{{ pipelineStatusText(r) }}</span>
            <div v-if="joinPaymentHintText(r)" class="flow-sub-hint">{{ joinPaymentHintText(r) }}</div>
          </td>
            <td class="col-audit">
            <div>{{ r.reviewer || '—' }}</div>
            <div>{{ formatTime(r.reviewAt) }}</div>
            <div class="audit-note">{{ r.reviewNote || '—' }}</div>
          </td>
            <td class="col-time">{{ formatTime(r.createdAt) }}</td>
            <td class="col-sticky-right col-ops">
              <div class="col-ops-grid">
                <button type="button" class="op-btn op-btn--view" @click="openPreview(r)">预览</button>
                <button type="button" class="op-btn op-btn--edit" :disabled="!canEdit" @click="openEditPayload(r)">编辑</button>
            <button
              v-if="useMembershipApplyMode && canApproveApply(r)"
              type="button"
                  class="op-btn op-btn--primary"
              title="表审通过并发送确认函"
              @click="openApproveDialog(r)"
            >
              表审通过并发确认函
            </button>
            <button
              v-if="useMembershipApplyMode && canUploadJoinPaymentVoucher(r)"
              type="button"
                  class="op-btn op-btn--primary"
              @click="openJoinPaymentUpload(r)"
            >
              帮他上传凭证
            </button>
            <button
              v-if="useMembershipApplyMode && canReviewJoinPayment(r)"
              type="button"
                  class="op-btn op-btn--primary"
              @click="openJoinPaymentReview(r)"
            >
              审核缴费凭证
            </button>
            <button
              v-if="useMembershipApplyMode && canViewJoinPaymentVoucher(r)"
              type="button"
                  class="op-btn op-btn--fee"
              @click="openJoinPaymentReview(r, true)"
            >
              {{ joinPaymentViewButtonLabel(r) }}
            </button>
                <button v-if="!useMembershipApplyMode" type="button" class="op-btn op-btn--primary" @click="onReview(r, true)">通过</button>
                <button v-if="!useMembershipApplyMode" type="button" class="op-btn op-btn--danger" @click="onReview(r, false)">驳回</button>
                <button type="button" class="op-btn op-btn--export" @click="downloadWord(r.id)">导出 Word</button>
              </div>
          </td>
        </tr>
      </tbody>
    </table>
    </div>
    <p v-else-if="canRenderRows && !loading" class="muted">暂无记录</p>
    <p v-else class="muted">{{ emptyHint }}</p>

    <ListPager
      v-if="canRenderRows"
      v-model:page="pagerPage"
      v-model:page-size="pagerSize"
      :total="totalElements"
    />

    <div v-if="approveDialogVisible" class="mask mask--stack" @click.self="closeApproveDialog">
      <div class="dialog dialog--shell dialog-secretary">
        <div class="dialog-head">
          <h3>表审通过（确认函）</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeApproveDialog">×</button>
        </div>
        <div class="dialog-body">
          <p class="dialog-hint">审核通过后将下发确认函，并生成「入会首次会费」订单；会员在「我的订单」上传缴费凭证。</p>
          <label class="stack-label">审核备注</label>
          <textarea v-model.trim="approveNote" class="stack-input" rows="3" placeholder="表审通过说明"></textarea>
          <label class="stack-label">确认函模板</label>
          <FancySelect
            v-model="approveConfirmTemplateId"
            icon="📄"
            class="stack-select"
            :options="confirmTemplateSelectOptions"
            placeholder="请选择确认函模板"
            @update:model-value="onApproveTemplateChange"
          />
          <template v-if="approvePlaceholders.length">
            <p class="dialog-hint muted">请填写确认函占位内容（与模版中双花括号占位名对应）</p>
            <div v-for="ph in approvePlaceholders" :key="ph" class="confirm-fill-row">
              <label class="stack-label">{{ ph }}</label>
              <input v-model.trim="approveFillValues[ph]" class="stack-input" type="text" :placeholder="`请填写${ph}`" />
            </div>
          </template>
          <p v-else-if="approveFillLoading" class="muted">正在读取模版占位…</p>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-edit" @click="closeApproveDialog">取消</button>
          <button type="button" class="btn btn-primary" @click="submitApproveDialog">确定</button>
        </div>
      </div>
    </div>

    <div v-if="joinPaymentDlg.visible" class="mask mask--stack" @click.self="closeJoinPaymentReview">
      <div class="dialog dialog--shell dialog-fee-voucher" @click.stop>
        <div class="dialog-head">
          <h3>{{ joinPaymentDialogTitle }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeJoinPaymentReview">×</button>
        </div>
        <div class="dialog-body">
          <p class="dialog-hint muted">{{ joinPaymentDlg.meta }}</p>
          <p v-if="joinPaymentDlg.rejected && joinPaymentDlg.rejectNote" class="reject-note">驳回原因：{{ joinPaymentDlg.rejectNote }}</p>
          <div class="voucher-img-wrap">
            <img v-if="joinPaymentDlg.img" class="voucher-img" :src="joinPaymentDlg.img" alt="缴费凭证">
            <template v-else-if="joinPaymentDlg.canAdminUpload">
              <button
                type="button"
                class="voucher-upload-add"
                :disabled="joinPaymentDlg.busy"
                @click="pickJoinPaymentVoucherFile"
              >
                <span class="voucher-upload-plus">+</span>
                <span class="voucher-upload-label">上传缴费凭证</span>
              </button>
              <p v-if="joinPaymentDlg.mockVoucher" class="muted mock-voucher-hint">
                小程序为线上支付，请补录到账凭证截图后再审核
              </p>
            </template>
            <p v-else class="muted">暂无凭证图片（请确认会员已在会费订单中上传）</p>
            <input
              ref="joinPaymentVoucherFileInput"
              type="file"
              accept="image/*,application/pdf,.doc,.docx"
              class="hidden-file"
              @change="onJoinPaymentVoucherPicked"
            >
          </div>
          <template v-if="joinPaymentDlg.viewOnly">
            <p v-if="joinPaymentDlg.rejected" class="muted dlg-foot-hint">该笔入会缴费已被驳回，请查看上方驳回原因与凭证。</p>
            <p v-else class="muted dlg-foot-hint">该笔入会缴费已审核通过，仅可查看凭证。</p>
          </template>
        </div>
        <div v-if="joinPaymentDlg.canReview" class="dialog-foot">
          <button type="button" class="btn btn-primary" :disabled="joinPaymentDlg.busy" @click="submitJoinPaymentApprove">审核通过</button>
          <button type="button" class="btn btn-delete" :disabled="joinPaymentDlg.busy" @click="submitJoinPaymentReject">驳回</button>
        </div>
        <div v-else-if="joinPaymentDlg.viewOnly" class="dialog-foot">
          <button type="button" class="btn btn-edit" @click="closeJoinPaymentReview">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="previewVisible" class="mask" @click.self="closePreview">
      <div class="dialog dialog--shell dialog-preview">
        <div class="dialog-head">
          <h3>申请详情预览</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closePreview">×</button>
        </div>
        <div class="dialog-body">
        <div v-if="previewLoading" class="muted">加载中…</div>
        <div v-else-if="previewRow" class="preview-meta">
          <div>记录ID：{{ previewRow.id }}</div>
          <div>填表人（申请人）：{{ previewRow.submitterName || previewRow.applicantName || '—' }}</div>
          <div>申请人单位：{{ previewRow.registerCompanyName || '—' }}</div>
          <div>会员代表：{{ previewRow.representativeName || '—' }}</div>
          <div v-if="previewApplyUnit(previewRow)">入会单位：{{ previewApplyUnit(previewRow) }}</div>
          <div>提交时间：{{ formatTime(previewRow.createdAt) }}</div>
          <div v-if="pipelineStatusText(previewRow)">流程状态：{{ pipelineStatusText(previewRow) }}</div>
          <div v-if="previewApplyRejectNote(previewRow)" class="preview-reject-wrap">
            <span class="preview-reject-label">驳回原因</span>
            <span class="preview-reject-text">{{ previewApplyRejectNote(previewRow) }}</span>
          </div>
          <div v-if="editFeeRejectNote(previewRow)" class="preview-reject-wrap">
            <span class="preview-reject-label">缴费驳回原因</span>
            <span class="preview-reject-text">{{ editFeeRejectNote(previewRow) }}</span>
          </div>
        </div>
        <div v-if="!previewLoading" class="preview-list">
          <div
            v-for="(item, idx) in previewItems"
            :key="`${idx}-${item.key}`"
            class="preview-item"
            :class="{ 'preview-item--group': String(item.key || '').startsWith('【') }"
          >
            <div class="preview-k">{{ item.key }}</div>
            <div class="preview-v">
              <template v-if="item.viewType === 'images'">
                <div class="media-grid">
                  <img
                    v-for="(u, i) in item.urls"
                    :key="`${item.key}-img-${i}`"
                    class="media-thumb"
                    :src="u"
                    alt="img"
                    @click="openLink(u)"
                  />
                </div>
              </template>
              <template v-else-if="item.viewType === 'file'">
                <button type="button" class="btn btn-edit" @click="openLink(item.fileUrl)">查看文件</button>
                <span class="file-name">{{ item.fileName || '附件' }}</span>
              </template>
              <template v-else>
                {{ item.value }}
              </template>
            </div>
          </div>
          <p v-if="!previewItems.length" class="muted">暂无可展示字段</p>
        </div>
        </div>
        <div v-if="previewRow && useMembershipApplyMode" class="dialog-foot preview-foot">
          <button
            v-if="canApproveApply(previewRow)"
            type="button"
            class="btn btn-primary"
            @click="openApproveDialog(previewRow)"
          >表审通过并发函</button>
          <button
            v-if="canApproveApply(previewRow)"
            type="button"
            class="btn btn-delete"
            @click="rejectApplyFromPreview"
          >驳回申请表</button>
          <button
            v-if="canUploadJoinPaymentVoucher(previewRow)"
            type="button"
            class="btn btn-primary"
            @click="openJoinPaymentUpload(previewRow)"
          >帮他上传凭证</button>
          <button
            v-if="canReviewJoinPayment(previewRow)"
            type="button"
            class="btn btn-primary"
            @click="openJoinPaymentReview(previewRow)"
          >审核缴费凭证</button>
          <button
            v-if="canViewJoinPaymentVoucher(previewRow)"
            type="button"
            class="btn btn-edit"
            @click="openJoinPaymentReview(previewRow, true)"
          >{{ joinPaymentViewButtonLabel(previewRow) }}</button>
          <button type="button" class="btn btn-edit" @click="closePreview">关闭</button>
        </div>
        <div v-else class="dialog-foot">
          <button type="button" class="btn btn-edit" @click="closePreview">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="editVisible" class="mask" @click.self="closeEditPayload">
      <div class="dialog dialog--shell dialog-edit">
        <div class="dialog-head">
          <h3>编辑入会申请表</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeEditPayload">×</button>
        </div>
        <div class="dialog-body">
        <p class="dialog-hint muted">
          按表单字段名称填写，保存后写入该条申请；审核状态、流程进度由系统保留。若已通过审核，将同步更新小程序「会员单位」卡片。
        </p>
        <div v-if="editRow" class="preview-meta">
          <div>记录ID：{{ editRow.id }}</div>
          <div>填表人（申请人）：{{ editRow.submitterName || editRow.applicantName || '—' }}</div>
          <div>申请人单位：{{ editRow.registerCompanyName || '—' }}</div>
          <div>会员代表：{{ editRow.representativeName || '—' }}</div>
          <div v-if="previewApplyUnit(editRow)">入会单位：{{ previewApplyUnit(editRow) }}</div>
          <div>提交时间：{{ formatTime(editRow.createdAt) }}</div>
          <div v-if="pipelineStatusText(editRow)">流程状态：{{ pipelineStatusText(editRow) }}</div>
          <div v-if="previewApplyRejectNote(editRow)" class="preview-reject-wrap">
            <span class="preview-reject-label">驳回原因</span>
            <span class="preview-reject-text">{{ previewApplyRejectNote(editRow) }}</span>
          </div>
          <div v-if="editFeeRejectNote(editRow)" class="preview-reject-wrap">
            <span class="preview-reject-label">缴费驳回原因</span>
            <span class="preview-reject-text">{{ editFeeRejectNote(editRow) }}</span>
          </div>
        </div>
        <div v-if="editLoading" class="muted">加载表单…</div>
        <div v-else class="edit-form-scroll">
          <div v-for="(grp, gi) in editGroups" :key="`eg-${gi}`" class="edit-group">
            <h4 class="edit-group-title">{{ grp.groupName }}</h4>
            <div
              v-for="(fld, fi) in grp.fields"
              v-show="shouldShowEditField(fld)"
              :key="`ef-${gi}-${fi}-${fld.fieldKey}`"
              class="edit-field"
            >
              <label class="edit-label">
                {{ fld.label }}
                <span v-if="fld.required" class="req">*</span>
              </label>
              <template v-if="fld.viewType === 'images'">
                <div class="edit-upload-row">
                  <div
                    v-for="(u, ui) in fld.previewUrls"
                    :key="`img-${gi}-${fi}-${ui}`"
                    class="upload-box upload-box--thumb"
                    title="点击更换图片"
                    @click="pickEditFieldUpload(fld, 'image', ui)"
                  >
                    <img class="upload-img" :src="u" alt="" />
                  </div>
                  <div
                    v-if="canAddEditImage(fld)"
                    class="upload-box upload-box--add"
                    :class="{ 'is-busy': fld.uploading }"
                    title="上传图片"
                    @click="pickEditFieldUpload(fld, 'image')"
                  >
                    <div v-if="fld.uploading" class="upload-placeholder">
                      <div class="upload-text">上传中…</div>
                    </div>
                    <div v-else class="upload-placeholder">
                      <div class="upload-plus">+</div>
                      <div class="upload-text">上传图片</div>
                    </div>
                  </div>
                </div>
                <p class="edit-upload-hint">{{ editImageFieldHint(fld) }}</p>
              </template>
              <template v-else-if="fld.viewType === 'file'">
                <div class="edit-upload-row">
                  <div
                    v-if="fld.fileUrl"
                    class="upload-box upload-box--file"
                    title="点击查看附件"
                    @click="openLink(fld.fileUrl)"
                  >
                    <span class="upload-file-name">{{ fld.fileName || '已上传附件' }}</span>
                  </div>
                  <div
                    class="upload-box upload-box--add"
                    :class="{ 'is-busy': fld.uploading }"
                    title="上传文件"
                    @click="pickEditFieldUpload(fld, 'file')"
                  >
                    <div v-if="fld.uploading" class="upload-placeholder">
                      <div class="upload-text">上传中…</div>
                    </div>
                    <div v-else class="upload-placeholder">
                      <div class="upload-plus">+</div>
                      <div class="upload-text">{{ fld.fileUrl ? '更换文件' : '上传文件' }}</div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else-if="fld.viewType === 'textarea'">
                <textarea v-model="fld.value" class="edit-textarea" rows="3" :placeholder="`请填写${fld.label}`" />
              </template>
              <template v-else-if="fld.readonly">
                <input
                  class="edit-input"
                  type="text"
                  :value="editMemberMasterLevel || fld.value || '—'"
                  disabled
                  readonly
                />
                <p class="muted edit-readonly-hint">正式会员级别已定，申请职务不可改（与会员主档「会员级别」一致）</p>
              </template>
              <template v-else-if="fld.fieldType === 'RADIO' && fld.selectOptions.length">
                <div class="edit-option-group">
                  <label
                    v-for="opt in fld.selectOptions"
                    :key="`${fld.fieldKey}-radio-${opt.value}`"
                    class="edit-option"
                    :class="{ 'edit-option--on': fld.value === opt.value }"
                  >
                    <input v-model="fld.value" type="radio" :name="`edit-${fld.fieldKey}`" :value="opt.value" />
                    <span>{{ opt.label }}</span>
                  </label>
                </div>
              </template>
              <template v-else-if="fld.fieldType === 'SELECT' && fld.selectOptions.length">
                <FancySelect
                  v-model="fld.value"
                  :options="fld.selectOptions"
                  :placeholder="`请选择${fld.label}`"
                  :searchable="fld.selectOptions.length > 6"
                />
              </template>
              <template v-else-if="isEditCascadeField(fld) && fld.cascadeCategories.length">
                <CascadeMultiSelect
                  v-model="fld.checkboxValues"
                  :categories="fld.cascadeCategories"
                  :title="fld.label"
                  :placeholder="`请选择${fld.label}`"
                />
              </template>
              <template v-else-if="isEditCheckField(fld) && fld.selectOptions.length">
                <div class="edit-option-group edit-option-group--wrap">
                  <label
                    v-for="opt in fld.selectOptions"
                    :key="`${fld.fieldKey}-chk-${opt.value}`"
                    class="edit-option edit-option--check"
                    :class="{ 'edit-option--on': editCheckboxChecked(fld, opt.value) }"
                  >
                    <input
                      type="checkbox"
                      :checked="editCheckboxChecked(fld, opt.value)"
                      @change="toggleEditCheckbox(fld, opt.value)"
                    />
                    <span>{{ opt.label }}</span>
                  </label>
                </div>
              </template>
              <template v-else-if="fld.selectOptions && fld.selectOptions.length">
                <FancySelect
                  v-model="fld.value"
                  :options="fld.selectOptions"
                  :placeholder="`请选择${fld.label}`"
                  :searchable="fld.selectOptions.length > 6"
                />
              </template>
              <template v-else>
                <input
                  v-model="fld.value"
                  class="edit-input"
                  type="text"
                  :placeholder="`请填写${fld.label}`"
                  :disabled="!!fld.readonly"
                  :readonly="!!fld.readonly"
                />
                <p v-if="fld.readonly" class="muted edit-readonly-hint">正式会员级别已定，申请职务不可改</p>
              </template>
            </div>
          </div>
          <p v-if="!editGroups.length" class="muted">未加载到表单字段，请先在「表单管理」配置入会申请表。</p>
        </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-edit" @click="closeEditPayload">取消</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="editSaving || editLoading || !canEdit || !editGroups.length"
            @click="saveEditPayload"
          >
            {{ editSaving ? '保存中…' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm, adminPrompt } from '@/utils/adminDialog'
import {
  getMembershipApplySubmissionDetail,
  getMembershipApplySubmissions,
  updateMembershipApplySubmissionPayload,
  membershipApplySubmissionExportWordUrl,
  reviewMembershipApplySubmission,
  reviewMembershipApplyPayment,
  attachMembershipApplyFeeVoucher,
  attachAdminMemberOrderVoucher,
  approveMembershipApplyWithNotices,
  getMembershipConfirmTemplates,
  getMembershipConfirmFillSchema,
  BASE_URL,
  getForms,
  formSubmissionExportWordUrl,
  getFormFields,
  getFormGroups,
  getFormSubmissionDetail,
  getFormSubmissions,
  reviewFormSubmission,
  uploadImage,
  uploadFile
} from '@/api'
import FancySelect from '@/components/FancySelect.vue'
import CascadeMultiSelect from '@/components/CascadeMultiSelect.vue'
import ListPager from '@/components/ListPager.vue'
import { hasAnyPermission } from '@/utils/adminPermission'
import backAboutIcon from '@/assets/icons/back-about.svg'
import backAboutActiveIcon from '@/assets/icons/back-about-active.svg'

const ADMIN_TOKEN_KEY = 'admin.token'

/** 同步 URL 筛选时避免 $route.query 监听重复触发 */
let skipRouteQueryWatch = false

export default {
  name: 'FormSubmissionsView',
  components: { FancySelect, CascadeMultiSelect, ListPager },
  data() {
    return {
      forms: [],
      formId: 0,
      rows: [],
      page: 0,
      size: 10,
      totalPages: 0,
      totalElements: 0,
      loading: false,
      errorMsg: '',
      applicantNameQ: '',
      applicantUnitQ: '',
      startTimeQ: '',
      endTimeQ: '',
      reviewFilter: '',
      previewVisible: false,
      previewLoading: false,
      previewRow: null,
      previewItems: [],
      fieldDefs: [],
      groupDefs: [],
      initialFormCode: '',
      initialFormId: 0,
      lockByRoute: false,
      backBtnHover: false,
      backAboutIcon,
      backAboutActiveIcon,
      confirmTemplates: [],
      batchConfirmTemplateId: '',
      selectedSubmissionIds: [],
      approveDialogVisible: false,
      approveDialogMode: 'single',
      approveRow: null,
      approveNote: '表审通过',
      approveConfirmTemplateId: '',
      approvePlaceholders: [],
      approveFillValues: {},
      approveFillLoading: false,
      joinPaymentDlg: {
        visible: false,
        row: null,
        submissionId: 0,
        feeOrderId: 0,
        meta: '',
        img: '',
        mockVoucher: false,
        canAdminUpload: false,
        canReview: false,
        viewOnly: false,
        uploadOnly: false,
        rejected: false,
        rejectNote: '',
        busy: false
      },
      previewDetail: null,
      canEdit: false,
      editVisible: false,
      editRow: null,
      editLoading: false,
      editGroups: [],
      editSaving: false,
      editApplyDutyLocked: false,
      editMemberMasterLevel: '',
      /** 从会员管理跳转时的行高亮（本地状态，点击其他区域可取消） */
      highlightedSubmissionId: 0,
    }
  },
  computed: {
    formSelectOptions() {
      const list = (this.forms || []).map((f) => ({
        value: Number(f.id),
        label: `${f.name}（${f.code}）`
      }))
      return [{ value: 0, label: '请选择表单' }, ...list]
    },
    reviewStatusOptions() {
      if (!this.useMembershipApplyMode) {
        return [
          { value: '', label: '全部' },
          { value: '待审核', label: '待审核' },
          { value: '已通过', label: '已通过' },
          { value: '已驳回', label: '已驳回' }
        ]
      }
      return [
        { value: '', label: '全部' },
        { value: '待审核申请表', label: '待审核申请表' },
        { value: '申请表被驳回', label: '申请表被驳回' },
        { value: '待缴费', label: '待缴费' },
        { value: '待审核入会缴费', label: '待审核入会缴费' },
        { value: '入会缴费被驳回', label: '入会缴费被驳回' },
        { value: '已通过', label: '已通过' }
      ]
    },
    joinPaymentDialogTitle() {
      const d = this.joinPaymentDlg || {}
      if (d.viewOnly) return '查看缴费凭证'
      if (d.uploadOnly) return '帮他上传入会缴费凭证'
      return '审核入会缴费凭证'
    },
    displayRows() {
      const list = Array.isArray(this.rows) ? this.rows : []
      return list.slice().sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
    },
    isFormLocked() {
      return true
    },
    useMembershipApplyMode() {
      return true
    },
    pageTitle() {
      return this.useMembershipApplyMode ? '入会申请记录' : '表单申请记录'
    },
    showBackButton() {
      return this.useMembershipApplyMode
    },
    queryDisabled() {
      return !this.useMembershipApplyMode && !this.formId
    },
    canRenderRows() {
      return this.useMembershipApplyMode || !!this.formId
    },
    emptyHint() {
      return this.useMembershipApplyMode ? '暂无记录' : '请选择表单后刷新'
    },
    tipText() {
      return '入会申请六段流程：待审核申请表 → 表审通过发确认函 → 待缴费 → 待审核入会缴费 → 已通过。首次会费订单标题为「入会首次会费」，与「审核缴费凭证」为同一笔。'
    },
    confirmTemplateSelectOptions() {
      return (this.confirmTemplates || [])
        .filter((x) => String(x.statusCode || '').toUpperCase() === 'ENABLED')
        .map((x) => ({
          value: Number(x.id),
          label: `${x.templateName || x.id}${x.applyLevel ? `（${x.applyLevel}）` : ''}`
        }))
    },
    approveSelectableRows() {
      return (this.displayRows || []).filter((r) => this.canApproveApply(r))
    },
    allApproveSelectableChecked() {
      const list = this.approveSelectableRows
      return list.length > 0 && list.every((r) => this.selectedSubmissionIds.includes(r.id))
    },
    batchApproveSelectedRows() {
      return (this.displayRows || []).filter(
        (r) => this.selectedSubmissionIds.includes(r.id) && this.canApproveApply(r)
      )
    },
    pagerPage: {
      get() {
        return this.page + 1
      },
      set(v) {
        const next = Math.max(1, Number(v) || 1)
        this.page = next - 1
        this.reload()
      },
    },
    pagerSize: {
      get() {
        return this.size
      },
      set(v) {
        this.size = Math.max(1, Number(v) || 10)
        this.page = 0
        this.reload()
      },
    },
    rowOffset() {
      return this.page * this.size
    },
  },
  async mounted() {
    this.canEdit = hasAnyPermission(['member.manage', 'member.edit', 'form.manage', 'form.edit'])
    this.initialFormCode = String(this.$route?.query?.formCode || '').trim() || 'membership_apply'
    this.initialFormId = Number(this.$route?.query?.formId || 0)
    this.lockByRoute = true
    this.applyFiltersFromRoute()
    await this.loadForms()
    this.applyInitialFormFromRoute()
    await this.loadConfirmTemplates()
    await this.reload()
    await this.tryOpenSubmissionFromRoute()
    await this.tryOpenEditFromRoute()
    await this.scrollToHighlightedSubmission()
  },
  async activated() {
    this.applyFiltersFromRoute()
    this.page = 0
    await this.reload()
    await this.tryOpenSubmissionFromRoute()
    await this.tryOpenEditFromRoute()
    await this.scrollToHighlightedSubmission()
  },
  methods: {
    applyFiltersFromRoute(route = this.$route) {
      const q = route?.query || {}
      this.applicantNameQ = q.applicantName != null ? String(q.applicantName) : ''
      this.applicantUnitQ = q.applicantUnit != null ? String(q.applicantUnit) : ''
      this.reviewFilter = q.reviewFilter != null ? String(q.reviewFilter) : ''
    },
    buildFilterQuery() {
      const q = { ...(this.$route?.query || {}) }
      const setOrDel = (key, val) => {
        const t = val == null ? '' : String(val).trim()
        if (t) q[key] = t
        else delete q[key]
      }
      setOrDel('reviewFilter', this.reviewFilter)
      setOrDel('applicantName', this.applicantNameQ)
      setOrDel('applicantUnit', this.applicantUnitQ)
      return q
    },
    async pushFiltersToRoute() {
      const query = this.buildFilterQuery()
      const resolved = this.$router.resolve({ path: this.$route.path, query })
      if (resolved.fullPath === this.$route.fullPath) return
      try {
        await this.$router.replace({ path: this.$route.path, query })
      } catch {
        /* ignore */
      }
    },
    async onFilterSelectChange() {
      if (!this.useMembershipApplyMode && !this.formId) return
      this.page = 0
      skipRouteQueryWatch = true
      try {
        await this.pushFiltersToRoute()
        await this.reload()
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async loadConfirmTemplates() {
      if (!this.useMembershipApplyMode) return
      try {
        this.confirmTemplates = (await getMembershipConfirmTemplates('ENABLED')) || []
      } catch {
        this.confirmTemplates = []
      }
    },
    pipelineStatusText(row) {
      return (
        String(row?.pipelineStatusLabel || row?.flowStatus || row?.reviewStatus || '').trim() || '待审核申请表'
      )
    },
    joinPaymentHintText(row) {
      return String(row?.joinPaymentHint || '').trim()
    },
    canApproveApply(row) {
      return this.pipelineStatusText(row) === '待审核申请表'
    },
    canUploadJoinPaymentVoucher(row) {
      if (!row) return false
      if (typeof row.canUploadJoinPaymentVoucher === 'boolean') return row.canUploadJoinPaymentVoucher
      return this.pipelineStatusText(row) === '待缴费'
    },
    canReviewJoinPayment(row) {
      if (!row) return false
      if (typeof row.canReviewJoinPayment === 'boolean') return row.canReviewJoinPayment
      return this.pipelineStatusText(row) === '待审核入会缴费'
    },
    canViewJoinPaymentVoucher(row) {
      if (!row || this.canReviewJoinPayment(row)) return false
      if (typeof row.canViewJoinPaymentVoucher === 'boolean') return row.canViewJoinPaymentVoucher
      const status = this.pipelineStatusText(row)
      return status === '已通过' || status === '入会缴费被驳回'
    },
    joinPaymentViewButtonLabel() {
      return '查看缴费凭证'
    },
    previewApplyRejectNote(row) {
      if (!row || this.pipelineStatusText(row) !== '申请表被驳回') return ''
      const d = this.previewDetail
      const audit =
        d && d.payload && typeof d.payload === 'object' && d.payload.__audit
          ? d.payload.__audit
          : {}
      return String(row.reviewNote || audit.reviewNote || '').trim()
    },
    toggleSubmissionChecked(submissionId, row) {
      if (!this.canApproveApply(row)) return
      const i = this.selectedSubmissionIds.indexOf(submissionId)
      if (i >= 0) this.selectedSubmissionIds.splice(i, 1)
      else this.selectedSubmissionIds.push(submissionId)
    },
    toggleCheckAllApproveSelectable() {
      const eligible = this.approveSelectableRows
      if (!eligible.length) return
      const allOn = eligible.every((r) => this.selectedSubmissionIds.includes(r.id))
      if (allOn) {
        const drop = new Set(eligible.map((r) => r.id))
        this.selectedSubmissionIds = this.selectedSubmissionIds.filter((id) => !drop.has(id))
        return
      }
      const next = new Set(this.selectedSubmissionIds)
      eligible.forEach((r) => next.add(r.id))
      this.selectedSubmissionIds = Array.from(next)
    },
    memberIdFromPayload(payload) {
      const flow = payload && payload.__flow
      const v = flow && flow.memberId
      if (v == null || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    openApproveDialog(row) {
      this.errorMsg = ''
      this.approveDialogMode = 'single'
      this.approveRow = row
      this.approveNote = '表审通过'
      const cOpts = this.confirmTemplateSelectOptions
      this.approveConfirmTemplateId = this.batchConfirmTemplateId || (cOpts.length ? cOpts[0].value : '')
      this.approveDialogVisible = true
      void this.loadApproveFillSchema()
    },
    openBatchApproveDialog() {
      const confirmTemplateId = Number(this.batchConfirmTemplateId)
      if (!confirmTemplateId) {
        this.errorMsg = '请选择确认函模板'
        return
      }
      const rows = this.batchApproveSelectedRows
      if (!rows.length) {
        this.errorMsg = '请勾选「待审核申请表」记录'
        return
      }
      this.errorMsg = ''
      this.approveDialogMode = 'batch'
      this.approveRow = null
      this.approveNote = '表审通过'
      this.approveConfirmTemplateId = this.batchConfirmTemplateId
      this.approveDialogVisible = true
      void this.loadApproveFillSchema()
    },
    onApproveTemplateChange() {
      void this.loadApproveFillSchema()
    },
    async loadApproveFillSchema() {
      const templateId = Number(this.approveConfirmTemplateId)
      if (!templateId) {
        this.approvePlaceholders = []
        this.approveFillValues = {}
        return
      }
      this.approveFillLoading = true
      try {
        const schema = await getMembershipConfirmFillSchema(templateId)
        const placeholders = Array.isArray(schema && schema.placeholders) ? schema.placeholders : []
        const lastFill = schema && schema.lastFill && typeof schema.lastFill === 'object' ? schema.lastFill : {}
        this.approvePlaceholders = placeholders
        const next = {}
        placeholders.forEach((ph) => {
          const key = String(ph || '').trim()
          if (!key) return
          next[key] = String(lastFill[key] || '').trim()
        })
        this.approveFillValues = next
      } catch (e) {
        this.approvePlaceholders = []
        this.approveFillValues = {}
        this.errorMsg = (e && e.message) || '读取确认函占位失败'
      } finally {
        this.approveFillLoading = false
      }
    },
    buildApproveFillPayload() {
      const out = {}
      ;(this.approvePlaceholders || []).forEach((ph) => {
        const key = String(ph || '').trim()
        if (!key) return
        out[key] = String((this.approveFillValues && this.approveFillValues[key]) || '').trim()
      })
      return out
    },
    closeApproveDialog() {
      this.approveDialogVisible = false
      this.approveRow = null
      this.approveDialogMode = 'single'
      this.approvePlaceholders = []
      this.approveFillValues = {}
    },
    async submitApproveDialog() {
      const note = String(this.approveNote || '').trim()
      const confirmTemplateId = Number(this.approveConfirmTemplateId)
      if (!note) {
        this.errorMsg = '请填写审核备注'
        return
      }
      if (!confirmTemplateId) {
        this.errorMsg = '请选择确认函模板'
        return
      }
      for (const ph of this.approvePlaceholders || []) {
        const key = String(ph || '').trim()
        if (!key) continue
        if (!String((this.approveFillValues && this.approveFillValues[key]) || '').trim()) {
          this.errorMsg = `请填写确认函占位：${key}`
          return
        }
      }
      const fillValues = this.buildApproveFillPayload()
      const payload = {
        confirmTemplateId,
        reviewNote: note,
        reviewer: '管理员',
        fillValues,
      }
      try {
        this.errorMsg = ''
        if (this.approveDialogMode === 'batch') {
          const rows = this.batchApproveSelectedRows
          if (!rows.length) {
            this.errorMsg = '请勾选「待审核申请表」记录'
            return
          }
          if (!await adminConfirm(`将对 ${rows.length} 条申请执行表审通过并下发确认函？`)) return
          let ok = 0
          const failIds = []
          for (let i = 0; i < rows.length; i += 1) {
            const row = rows[i]
            try {
              await approveMembershipApplyWithNotices(row.id, payload)
              ok += 1
            } catch {
              failIds.push(row.id)
            }
          }
          this.selectedSubmissionIds = []
          this.closeApproveDialog()
          await this.reload()
          let msg = `批量完成：成功 ${ok} 条。`
          if (failIds.length) msg += ` 失败 ${failIds.length} 条（ID: ${failIds.join(', ')}）。`
          window.alert(msg)
          return
        }
        const row = this.approveRow
        if (!row) return
        await approveMembershipApplyWithNotices(row.id, payload)
        this.closeApproveDialog()
        await this.reload()
        window.alert('表审通过，已下发确认函')
      } catch (e) {
        this.errorMsg = e.message || '操作失败'
      }
    },
    async batchApproveWithNotices() {
      this.openBatchApproveDialog()
    },
    voucherAbs(url) {
      const raw = String(url || '').trim()
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      return raw.startsWith('/') ? `${BASE_URL}${raw}` : `${BASE_URL}/${raw}`
    },
    async openJoinPaymentUpload(row) {
      if (!row) return
      this.errorMsg = ''
      try {
        const detail = await getMembershipApplySubmissionDetail(row.id)
        const feeOrderId = Number(detail?.feeOrderId || 0)
        this.joinPaymentDlg = {
          visible: true,
          row,
          submissionId: row.id,
          feeOrderId,
          meta: `${row.applicantName || row.submitterName || '—'} · 入会首次会费 · 申请 #${row.id}`,
          img: '',
          mockVoucher: false,
          canAdminUpload: true,
          canReview: false,
          uploadOnly: true,
          viewOnly: false,
          busy: false
        }
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
      }
    },
    async openJoinPaymentReview(row, viewOnly = false) {
      if (!row) return
      this.errorMsg = ''
      try {
        const detail = await getMembershipApplySubmissionDetail(row.id)
        const url = String(detail?.feePaymentVoucherUrl || '').trim()
        const isMockVoucher = url.startsWith('mock://')
        const orderReviewing = String(detail?.feeOrderStatus || '').toUpperCase() === 'REVIEWING'
        const feeOrderId = Number(detail?.feeOrderId || 0)
        const readOnly = viewOnly || !this.canReviewJoinPayment(row)
        const rejected = this.pipelineStatusText(row) === '入会缴费被驳回'
        const rejectNote = rejected ? String(detail?.feePaymentReviewNote || '').trim() : ''
        this.joinPaymentDlg = {
          visible: true,
          row,
          submissionId: row.id,
          feeOrderId,
          meta: `${row.applicantName || row.submitterName || '—'} · 入会首次会费 · 申请 #${row.id}`,
          img: isMockVoucher ? '' : this.voucherAbs(url),
          mockVoucher: isMockVoucher,
          canAdminUpload: !readOnly && orderReviewing && feeOrderId > 0 && (isMockVoucher || !url),
          canReview: !readOnly && orderReviewing && (!!url || isMockVoucher),
          uploadOnly: false,
          viewOnly: readOnly,
          rejected,
          rejectNote,
          busy: false
        }
      } catch (e) {
        this.errorMsg = e.message || '加载缴费凭证失败'
      }
    },
    closeJoinPaymentReview() {
      this.joinPaymentDlg = {
        visible: false,
        row: null,
        submissionId: 0,
        feeOrderId: 0,
        meta: '',
        img: '',
        mockVoucher: false,
        canAdminUpload: false,
        canReview: false,
        uploadOnly: false,
        viewOnly: false,
        rejected: false,
        rejectNote: '',
        busy: false
      }
    },
    pickJoinPaymentVoucherFile() {
      const el = this.$refs.joinPaymentVoucherFileInput
      if (el && typeof el.click === 'function') el.click()
    },
    async onJoinPaymentVoucherPicked(e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (e && e.target) e.target.value = ''
      if (!file) return
      const orderId = Number(this.joinPaymentDlg.feeOrderId || 0)
      const submissionId = Number(this.joinPaymentDlg.submissionId || 0)
      if (!submissionId) {
        this.errorMsg = '未找到申请记录，无法上传凭证'
        return
      }
      this.joinPaymentDlg.busy = true
      this.errorMsg = ''
      try {
        const data = await uploadImage(file)
        const url = String((data && data.url) || '').trim()
        if (!url) throw new Error('未获取到图片地址')
        if (this.useMembershipApplyMode) {
          await attachMembershipApplyFeeVoucher(submissionId, { voucherUrl: url })
        } else if (orderId) {
          await attachAdminMemberOrderVoucher(orderId, { voucherUrl: url })
        } else {
          throw new Error('未找到会费订单，无法补录凭证')
        }
        this.joinPaymentDlg.img = this.voucherAbs(url)
        this.joinPaymentDlg.mockVoucher = false
        if (this.joinPaymentDlg.uploadOnly) {
          this.closeJoinPaymentReview()
          await this.reload()
          window.alert('凭证已上传，状态已更新为待审核入会缴费')
          return
        }
        this.joinPaymentDlg.canAdminUpload = false
        this.joinPaymentDlg.canReview = true
        this.joinPaymentDlg.uploadOnly = false
      } catch (err) {
        this.errorMsg = err.message || '上传凭证失败'
      } finally {
        this.joinPaymentDlg.busy = false
      }
    },
    async submitJoinPaymentApprove() {
      const id = Number(this.joinPaymentDlg.submissionId || 0)
      if (!id) return
      if (!await adminConfirm('确认通过该入会首次会费凭证？')) return
      this.joinPaymentDlg.busy = true
      try {
        await reviewMembershipApplyPayment(id, { paid: true, reviewNote: '会费凭证审核通过', reviewer: '管理员' })
        this.closeJoinPaymentReview()
        await this.reload()
        window.alert('已审核通过，会员已生效')
      } catch (e) {
        this.errorMsg = e.message || '审核失败'
      } finally {
        this.joinPaymentDlg.busy = false
      }
    },
    async submitJoinPaymentReject() {
      const id = Number(this.joinPaymentDlg.submissionId || 0)
      if (!id) return
      const note = await adminPrompt('驳回原因', '')
      if (note === null) return
      const finalNote = String(note || '').trim()
      if (!finalNote) {
        this.errorMsg = '请填写驳回原因'
        return
      }
      this.joinPaymentDlg.busy = true
      try {
        await reviewMembershipApplyPayment(id, { paid: false, reviewNote: finalNote, reviewer: '管理员' })
        this.closeJoinPaymentReview()
        await this.reload()
        window.alert('已驳回，申请人可重新缴费')
      } catch (e) {
        this.errorMsg = e.message || '驳回失败'
      } finally {
        this.joinPaymentDlg.busy = false
      }
    },
    async rejectApplyFromPreview() {
      const row = this.previewRow
      if (!row || !this.canApproveApply(row)) return
      await this.onReview(row, false)
      this.closePreview()
    },
    async loadForms() {
      if (this.useMembershipApplyMode) return
      try {
        this.forms = (await getForms()) || []
      } catch (e) {
        this.errorMsg = e.message || '读取表单列表失败'
      }
    },
    applyInitialFormFromRoute() {
      if (this.useMembershipApplyMode) return
      if (!Array.isArray(this.forms) || !this.forms.length) return
      if (this.formId) return
      if (this.initialFormId > 0) {
        const hit = this.forms.find((f) => Number(f.id) === this.initialFormId)
        if (hit) {
          this.formId = Number(hit.id)
          return
        }
      }
      if (this.initialFormCode) {
        const code = this.initialFormCode.toLowerCase()
        const hit = this.forms.find((f) => String(f.code || '').trim().toLowerCase() === code)
        if (hit) {
          this.formId = Number(hit.id)
        }
      }
    },
    async reload() {
      this.errorMsg = ''
      if (!this.useMembershipApplyMode && !this.formId) return
      this.loading = true
      try {
        if (this.useMembershipApplyMode) {
          const data = await getMembershipApplySubmissions(this.page, this.size, this.queryPayload())
          this.rows = (data && data.content) || []
          this.totalPages = (data && data.totalPages) || 0
          this.totalElements = Number(data && data.totalElements != null ? data.totalElements : this.rows.length) || 0
          const idSet = new Set(this.rows.map((r) => r.id))
          this.selectedSubmissionIds = this.selectedSubmissionIds.filter((id) => idSet.has(id))
        } else {
          await this.loadFormMeta()
          const data = await getFormSubmissions(this.formId, this.page, this.size, this.queryPayload())
          this.rows = (data && data.content) || []
          this.totalPages = (data && data.totalPages) || 0
        }
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
        this.rows = []
        this.totalPages = 0
        this.totalElements = 0
      } finally {
        this.loading = false
      }
    },
    async loadFormMeta() {
      if (!this.formId) return
      const [fields, groups] = await Promise.all([
        getFormFields(this.formId),
        getFormGroups(this.formId)
      ])
      this.fieldDefs = Array.isArray(fields) ? fields : []
      this.groupDefs = Array.isArray(groups) ? groups : []
    },
    formatTime(t) {
      if (t == null || t === '') return '—'
      let s = String(t).trim()
      if (!s) return '—'
      s = s.replace('T', ' ')
      // Spring LocalDateTime 等常带 .SSS 或更长小数秒；去掉小数秒与末尾 Z
      s = s.replace(/(\d{2}:\d{2}:\d{2})\.\d+/, '$1')
      s = s.replace(/Z$/i, '').trim()
      return s || '—'
    },
    queryPayload() {
      return {
        applicantName: this.applicantNameQ,
        applicantUnit: this.applicantUnitQ,
        reviewStatus: this.reviewFilter,
        startTime: this.normalizeFilterDateTime(this.startTimeQ, 'start'),
        endTime: this.normalizeFilterDateTime(this.endTimeQ, 'end'),
      }
    },
    normalizeFilterDateTime(raw, edge) {
      const s = String(raw || '').trim()
      if (!s) return ''
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return edge === 'end' ? `${s}T23:59:59` : `${s}T00:00:00`
      }
      return s
    },
    async onSearch() {
      this.page = 0
      skipRouteQueryWatch = true
      try {
        await this.pushFiltersToRoute()
        await this.reload()
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async onResetFilters() {
      this.reviewFilter = ''
      this.applicantNameQ = ''
      this.applicantUnitQ = ''
      this.startTimeQ = ''
      this.endTimeQ = ''
      this.page = 0
      skipRouteQueryWatch = true
      try {
        await this.pushFiltersToRoute()
        await this.reload()
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async downloadWord(submissionId) {
      this.errorMsg = ''
      const token = localStorage.getItem(ADMIN_TOKEN_KEY) || ''
      const url = this.useMembershipApplyMode
        ? membershipApplySubmissionExportWordUrl(submissionId)
        : formSubmissionExportWordUrl(submissionId, this.formId)
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        if (!res.ok) {
          let msg = `HTTP ${res.status}`
          try {
            const j = await res.json()
            if (j && j.message) msg = j.message
          } catch {
            // ignore
          }
          throw new Error(msg)
        }
        const blob = await res.blob()
        const cd = res.headers.get('Content-Disposition') || ''
        let filename = `submission-${submissionId}.docx`
        const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i)
        if (m) {
          filename = decodeURIComponent((m[1] || m[2] || filename).trim())
        }
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename
        a.click()
        URL.revokeObjectURL(a.href)
      } catch (e) {
        this.errorMsg = e.message || '导出失败'
      }
    },
    async batchExportWord() {
      this.errorMsg = ''
      const ids = this.displayRows.map((r) => r.id).filter((id) => id != null && id > 0)
      if (!ids.length) return
      for (let i = 0; i < ids.length; i += 1) {
        // 顺序下载，避免浏览器并发拦截
        // eslint-disable-next-line no-await-in-loop
        await this.downloadWord(ids[i])
      }
    },
    reviewPillClass(status) {
      const s = String(status || '')
      if (s.includes('驳回') || s === '欠费') return 'pill-red'
      if (s === '已通过' || s.includes('已缴费')) return 'pill-green'
      if (s.includes('待审核') || s === '待缴费' || s === '即将到期') return 'pill-gray'
      return 'pill-gray'
    },
    async onReview(row, approved) {
      const title = approved ? '审核通过' : '驳回申请'
      const defaultNote = approved ? '审核通过，允许申请' : ''
      const note = await adminPrompt(`${title}备注`, defaultNote)
      if (note === null) {
        this.errorMsg = '已取消审核（未填写备注并关闭了输入框）'
        return
      }
      const finalNote = String(note || '').trim()
      if (!approved && !finalNote) {
        this.errorMsg = '驳回时必须填写原因'
        return
      }
      if (approved && !finalNote) {
        this.errorMsg = '通过时建议填写审核意见'
        return
      }
      try {
        this.errorMsg = ''
        if (this.useMembershipApplyMode) {
          await reviewMembershipApplySubmission(row.id, {
            approved,
            reviewNote: finalNote,
            reviewer: '管理员'
          })
        } else {
          await reviewFormSubmission(row.id, {
            formId: this.formId,
            approved,
            reviewNote: finalNote,
            reviewer: '管理员'
          })
        }
        await this.reload()
      } catch (e) {
        this.errorMsg = e.message || '审核失败'
      }
    },
    goConfirmTemplateManage() {
      this.$router.push('/member/confirm-templates')
    },
    async openPreview(row) {
      this.previewVisible = true
      this.previewLoading = true
      this.previewRow = row
      this.previewItems = []
      try {
        if (this.useMembershipApplyMode) {
          await this.ensureMembershipFieldMeta()
        }
        const detail = this.useMembershipApplyMode
          ? await getMembershipApplySubmissionDetail(row.id)
          : await getFormSubmissionDetail(row.id, this.formId)
        this.previewDetail = detail
        const payload = detail && detail.payload && typeof detail.payload === 'object' ? detail.payload : {}
        if (this.useMembershipApplyMode && detail) {
          this.previewRow = this.enrichMembershipPreviewRow(row, detail, payload)
        }
        this.previewItems = this.buildFullFormPreview(payload)
      } catch (e) {
        this.errorMsg = e.message || '读取详情失败'
      } finally {
        this.previewLoading = false
      }
    },
    membershipPayloadUnit(payload) {
      const p = payload && typeof payload === 'object' ? payload : {}
      for (const k of ['companyName', 'applicantUnit', 'unitName']) {
        const s = String(p[k] || '').trim()
        if (s) return s
      }
      return ''
    },
    previewApplyUnit(row) {
      return String(row?.applyUnit || row?.applicantUnit || '').trim()
    },
    enrichMembershipPreviewRow(row, detail, payload) {
      const p = payload && typeof payload === 'object' ? payload : {}
      const pipeline =
        String(detail?.pipelineStatusLabel || row?.pipelineStatusLabel || row?.flowStatus || row?.reviewStatus || '').trim()
      return {
        ...row,
        createdAt: detail?.createdAt || row?.createdAt || null,
        submitterName:
          String(detail?.submitterName || row?.submitterName || p.submitterName || '').trim()
          || String(row?.applicantName || '').trim(),
        representativeName:
          String(detail?.representativeName || row?.representativeName || p.repName || '').trim(),
        registerCompanyName:
          String(detail?.registerCompanyName || row?.registerCompanyName || '').trim(),
        applyUnit:
          String(detail?.applicantUnit || row?.applicantUnit || '').trim()
          || this.membershipPayloadUnit(p),
        pipelineStatusLabel: pipeline,
        flowStatus: pipeline || row?.flowStatus,
        reviewStatus: pipeline || row?.reviewStatus,
        reviewNote: String(detail?.reviewNote || row?.reviewNote || p?.__audit?.reviewNote || '').trim(),
        feePaymentReviewNote: String(detail?.feePaymentReviewNote || row?.feePaymentReviewNote || '').trim(),
      }
    },
    editFeeRejectNote(row) {
      if (!row || this.pipelineStatusText(row) !== '入会缴费被驳回') return ''
      return String(row?.feePaymentReviewNote || '').trim()
    },
    async ensureMembershipFieldMeta() {
      if (Array.isArray(this.fieldDefs) && this.fieldDefs.length) return
      const forms = (await getForms()) || []
      const hit = forms.find((f) => String(f.code || '').trim() === 'membership_apply')
      const formId = Number(hit && hit.id ? hit.id : 0)
      if (!formId) return
      const [fields, groups] = await Promise.all([
        getFormFields(formId),
        getFormGroups(formId)
      ])
      this.fieldDefs = Array.isArray(fields) ? fields : []
      this.groupDefs = Array.isArray(groups) ? groups : []
    },
    closePreview() {
      this.previewVisible = false
      this.previewLoading = false
      this.previewRow = null
      this.previewItems = []
    },
    payloadForEditJson(payload) {
      const src = payload && typeof payload === 'object' ? { ...payload } : {}
      delete src.__audit
      delete src.__flow
      return src
    },
    parseFieldOptions(f) {
      const raw = f && (f.optionsJson != null ? f.optionsJson : f.options_json)
      const s = String(raw || '').trim()
      if (!s && raw == null) return []
      try {
        const arr = typeof raw === 'string' ? JSON.parse(s) : raw
        if (Array.isArray(arr)) {
          if (this.isCascadeFieldType(f) || arr.some((it) => it && typeof it === 'object' && Array.isArray(it.children))) {
            return this.flattenCascadeOptions(arr)
          }
          return this.normalizeFieldOptionList(arr)
        }
      } catch {
        // ignore
      }
      return []
    },
    isCascadeFieldType(f) {
      const ft = String(f?.fieldType || '').toUpperCase()
      if (ft === 'CASCADER_MULTI') return true
      const ui = String(f?.uiMode || '').trim().toUpperCase()
      return ui === 'CASCADE_MULTI'
    },
    normalizeFieldOptionList(arr) {
      return (arr || [])
        .map((it) => {
          if (it && typeof it === 'object') {
            const label = String(it.label || '').trim()
            const value = String(it.value != null ? it.value : label).trim()
            if (!label && !value) return null
            return { label: label || value, value: value || label }
          }
          const text = String(it || '').trim()
          if (!text) return null
          return { label: text, value: text }
        })
        .filter(Boolean)
    },
    parseCascadeCategories(f) {
      const raw = f && (f.optionsJson != null ? f.optionsJson : f.options_json)
      const s = String(raw || '').trim()
      if (!s && raw == null) return []
      try {
        const arr = typeof raw === 'string' ? JSON.parse(s) : raw
        if (!Array.isArray(arr)) return []
        const hasChildren = arr.some((it) => it && typeof it === 'object' && Array.isArray(it.children))
        if (!this.isCascadeFieldType(f) && !hasChildren) return []
        return arr
          .map((cat, i) => {
            const parentLabel = String(cat?.label || '').trim()
            const parentValue = String(cat?.value != null ? cat.value : (parentLabel || `cat_${i + 1}`)).trim()
            const children = (Array.isArray(cat?.children) ? cat.children : [])
              .map((c, j) => {
                const childLabel = String(c?.label || '').trim()
                const childValue = String(c?.value != null ? c.value : (childLabel || `${parentValue}_${j + 1}`)).trim()
                if (!childLabel && !childValue) return null
                return { label: childLabel || childValue, value: childValue || childLabel }
              })
              .filter(Boolean)
            if (!parentLabel && !children.length) return null
            return {
              label: parentLabel || `分类${i + 1}`,
              value: parentValue || `cat_${i + 1}`,
              desc: String(cat?.desc || '').trim(),
              children,
            }
          })
          .filter(Boolean)
      } catch {
        return []
      }
    },
    flattenCascadeOptions(arr) {
      const out = []
      ;(arr || []).forEach((cat, i) => {
        const parentLabel = String(cat?.label || '').trim()
        const parentValue = String(cat?.value || parentLabel || `cat_${i + 1}`).trim()
        const children = Array.isArray(cat?.children) ? cat.children : []
        children.forEach((c, j) => {
          const childLabel = String(c?.label || '').trim()
          const childValue = String(c?.value || childLabel || `${parentValue}_${j + 1}`).trim()
          if (!childLabel && !childValue) return
          const label = parentLabel ? `${parentLabel} / ${childLabel || childValue}` : (childLabel || childValue)
          out.push({ label, value: childValue || childLabel })
        })
      })
      return out
    },
    isEditCascadeField(fld) {
      const ft = String(fld?.fieldType || '').toUpperCase()
      if (ft === 'CASCADER_MULTI') return true
      return Array.isArray(fld?.cascadeCategories) && fld.cascadeCategories.length > 0
    },
    isEditCheckField(fld) {
      const ft = String(fld?.fieldType || '').toUpperCase()
      return ft === 'CHECKBOX' || ft === 'CHECKBOX_GROUP'
    },
    isPartyBranchToggleFieldMeta(fld) {
      const key = String(fld?.fieldKey || '').trim().toLowerCase()
      const label = String(fld?.label || '').trim()
      return key.includes('partyorg') || key.includes('party_building') || label.includes('是否有党支部')
    },
    isPartyBranchDependentFieldMeta(fld) {
      const key = String(fld?.fieldKey || '').trim().toLowerCase()
      const label = String(fld?.label || '').trim()
      if (this.isPartyBranchToggleFieldMeta(fld)) return false
      return label.includes('党建情况') || label.includes('党员人数') || key.includes('party_member_count')
    },
    isPartyMemberCountFieldMeta(fld) {
      const key = String(fld?.fieldKey || '').trim().toLowerCase()
      const label = String(fld?.label || '').trim()
      return label.includes('党员人数') || key.includes('party_member_count')
    },
    editPartyToggleIsNo() {
      for (const grp of this.editGroups || []) {
        for (const fld of grp.fields || []) {
          if (!this.isPartyBranchToggleFieldMeta(fld)) continue
          const v = String(fld.value || '').trim()
          if (v === '否' || /^no$/i.test(v)) return true
        }
      }
      return false
    },
    editPartyOrgIsNone() {
      for (const grp of this.editGroups || []) {
        for (const fld of grp.fields || []) {
          const label = String(fld?.label || '').trim()
          const key = String(fld?.fieldKey || '').trim().toLowerCase()
          if (this.isPartyBranchToggleFieldMeta(fld)) continue
          if (!label.includes('党建情况') && !key.includes('partyorg') && !label.includes('党组织')) continue
          const v = String(fld.value || '').trim()
          if (v === '无' || v === '无党支部' || /^无/.test(v)) return true
        }
      }
      return false
    },
    shouldShowEditField(fld) {
      if (!fld) return true
      if (!this.isPartyBranchDependentFieldMeta(fld)) return true
      if (this.editPartyToggleIsNo()) return false
      if (this.isPartyMemberCountFieldMeta(fld) && this.editPartyOrgIsNone()) return false
      return true
    },
    editCheckboxChecked(fld, value) {
      const arr = Array.isArray(fld?.checkboxValues) ? fld.checkboxValues : []
      return arr.includes(value)
    },
    toggleEditCheckbox(fld, value) {
      const arr = Array.isArray(fld?.checkboxValues) ? [...fld.checkboxValues] : []
      const idx = arr.indexOf(value)
      if (idx >= 0) arr.splice(idx, 1)
      else arr.push(value)
      fld.checkboxValues = arr
    },
    parseMultiFieldValue(raw) {
      if (Array.isArray(raw)) {
        return raw.map((x) => String(x == null ? '' : x).trim()).filter(Boolean)
      }
      const s = String(raw == null ? '' : raw).trim()
      if (!s) return []
      if (s.startsWith('[') && s.endsWith(']')) {
        try {
          const arr = JSON.parse(s)
          if (Array.isArray(arr)) {
            return arr.map((x) => String(x == null ? '' : x).trim()).filter(Boolean)
          }
        } catch {
          // ignore
        }
      }
      if (s.includes(',')) return s.split(',').map((x) => x.trim()).filter(Boolean)
      return [s]
    },
    resolvePayloadFieldRaw(payload, f) {
      const keyCandidates = [
        String(f.fieldKey || '').trim(),
        `field_${f.id}`,
        `fieldId:${f.id}`,
        `field_id_${f.id}`,
      ].filter(Boolean)
      for (let i = 0; i < keyCandidates.length; i += 1) {
        const k = keyCandidates[i]
        if (payload && Object.prototype.hasOwnProperty.call(payload, k)) {
          return payload[k]
        }
      }
      return ''
    },
    resolvePayloadFieldValue(payload, f) {
      const keyCandidates = [
        String(f.fieldKey || '').trim(),
        `field_${f.id}`,
        `fieldId:${f.id}`,
        `field_id_${f.id}`,
      ].filter(Boolean)
      for (let i = 0; i < keyCandidates.length; i += 1) {
        const k = keyCandidates[i]
        if (payload && Object.prototype.hasOwnProperty.call(payload, k)) {
          const raw = payload[k]
          if (raw == null) return ''
          if (Array.isArray(raw)) return raw.map((x) => String(x == null ? '' : x).trim()).filter(Boolean).join(',')
          if (typeof raw === 'object') return JSON.stringify(raw)
          return String(raw).trim()
        }
      }
      return ''
    },
    buildEditGroupsFromPayload(payload) {
      const groups = Array.isArray(this.groupDefs) ? [...this.groupDefs] : []
      groups.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
      const groupNameById = {}
      groups.forEach((g) => { groupNameById[String(g.id)] = g.groupName || '其他信息' })
      const fields = Array.isArray(this.fieldDefs) ? [...this.fieldDefs] : []
      fields.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
      const bucket = {}
      fields.forEach((f) => {
        const gid = f.groupId != null ? String(f.groupId) : '__other__'
        const gname = groupNameById[gid] || '其他信息'
        if (!bucket[gname]) bucket[gname] = []
        const ft = String(f.fieldType || '').toUpperCase()
        const raw = this.resolvePayloadFieldRaw(payload, f)
        const rawVal = this.resolvePayloadFieldValue(payload, f)
        let viewType = 'text'
        let previewUrls = []
        let fileUrl = ''
        let value = rawVal
        let checkboxValues = []
        if (ft === 'IMAGE') {
          viewType = 'images'
          previewUrls = this.normalizeImageUrls(rawVal)
          const multi = this.isEditMultiImageField({ fieldKey: String(f.fieldKey || '').trim(), label: f.label || '' })
          if (!multi && previewUrls.length > 1) {
            previewUrls = previewUrls.slice(0, 1)
            value = this.imagePartsFromValue(rawVal)[0] || ''
          }
        } else if (ft === 'FILE' || ft === 'ATTACHMENT') {
          viewType = 'file'
          fileUrl = this.toFullUrl(rawVal)
        } else if (ft === 'TEXTAREA' || ft === 'LONG_TEXT') {
          viewType = 'textarea'
        } else if (ft === 'CHECKBOX' || ft === 'CHECKBOX_GROUP' || ft === 'CASCADER_MULTI') {
          checkboxValues = this.parseMultiFieldValue(raw)
          value = ''
        } else if (Array.isArray(raw)) {
          value = raw.map((x) => String(x == null ? '' : x).trim()).filter(Boolean).join(',')
        } else {
          const full = this.toFullUrl(rawVal)
          if (full && this.looksLikeFileUrl(full)) {
            viewType = 'file'
            fileUrl = full
          }
        }
        const selectOptions = this.parseFieldOptions(f)
        const cascadeCategories = this.isCascadeFieldType(f) ? this.parseCascadeCategories(f) : []
        const readonly = this.isEditApplyDutyLockedField(f)
        if (readonly && this.editMemberMasterLevel) {
          value = this.editMemberMasterLevel
        }
        bucket[gname].push({
          label: f.label || f.fieldKey || '字段',
          fieldKey: String(f.fieldKey || `field_${f.id}`).trim(),
          fieldId: f.id,
          fieldType: ft,
          required: Boolean(f.requiredField),
          viewType,
          value,
          checkboxValues,
          previewUrls,
          fileUrl,
          fileName: fileUrl ? this.fileNameFromUrl(rawVal) : '',
          uploading: false,
          selectOptions,
          cascadeCategories,
          readonly,
        })
      })
      return Object.keys(bucket).map((groupName) => ({ groupName, fields: bucket[groupName] }))
    },
    buildPayloadFromEditGroups() {
      const out = {}
      const groups = this.editGroups || []
      groups.forEach((grp) => {
        const fields = grp.fields || []
        fields.forEach((fld) => {
          const key = String(fld.fieldKey || '').trim()
          if (!key) return
          if (!this.shouldShowEditField(fld)) return
          const ft = String(fld.fieldType || '').toUpperCase()
          if (this.isEditApplyDutyLockedField(fld) && this.editMemberMasterLevel) {
            out[key] = this.editMemberMasterLevel
            out.applyDuty = this.editMemberMasterLevel
            out.memberLevel = this.editMemberMasterLevel
            return
          }
          if (ft === 'CHECKBOX' || ft === 'CHECKBOX_GROUP' || ft === 'CASCADER_MULTI') {
            const arr = Array.isArray(fld.checkboxValues) ? fld.checkboxValues : []
            out[key] = arr.map((x) => String(x == null ? '' : x).trim()).filter(Boolean)
            return
          }
          let v = fld.value == null ? '' : String(fld.value).trim()
          if (fld.viewType === 'images' && v.includes(',')) {
            const parts = v.split(',').map((x) => x.trim()).filter(Boolean)
            out[key] = this.isEditMultiImageField(fld) ? parts : (parts[0] || '')
          } else {
            out[key] = v
          }
        })
      })
      return out
    },
    isEditApplyDutyLockedField(f) {
      if (!this.editApplyDutyLocked || !f) return false
      const key = String(f.fieldKey || '').trim().toLowerCase()
      const label = String(f.label || '').trim()
      return key === 'applyduty' || key === 'apply_duty' || label.includes('申请职务')
    },
    /** 把主档级别写入申请职务所有可能的 payload 键（含 field_id） */
    writeApplyDutyIntoPayload(payload, level) {
      const lvl = String(level || '').trim()
      if (!payload || typeof payload !== 'object' || !lvl) return
      payload.applyDuty = lvl
      payload.memberLevel = lvl
      const fields = Array.isArray(this.fieldDefs) ? this.fieldDefs : []
      fields.forEach((f) => {
        if (!this.isApplyDutyFieldDef(f)) return
        const key = String(f.fieldKey || '').trim()
        if (key) payload[key] = lvl
        if (f.id != null) {
          payload[`field_${f.id}`] = lvl
          payload[`fieldId:${f.id}`] = lvl
          payload[`field_id_${f.id}`] = lvl
        }
      })
    },
    isApplyDutyFieldDef(f) {
      if (!f) return false
      const key = String(f.fieldKey || '').trim().toLowerCase()
      const label = String(f.label || '').trim()
      return key === 'applyduty' || key === 'apply_duty' || label.includes('申请职务')
    },
    async openEditPayload(row) {
      if (!row || !row.id) return
      this.editRow = row
      this.editVisible = true
      this.editLoading = true
      this.editGroups = []
      this.editApplyDutyLocked = false
      this.editMemberMasterLevel = ''
      this.errorMsg = ''
      try {
        await this.ensureMembershipFieldMeta()
        const detail = await getMembershipApplySubmissionDetail(row.id)
        const payload = detail && detail.payload && typeof detail.payload === 'object' ? detail.payload : {}
        this.editApplyDutyLocked = !!(detail?.lockApplyDuty || detail?.lock_apply_duty)
        this.editMemberMasterLevel = String(
          detail?.memberMasterLevel || detail?.member_master_level || '',
        ).trim()
        if (this.editApplyDutyLocked && this.editMemberMasterLevel) {
          this.writeApplyDutyIntoPayload(payload, this.editMemberMasterLevel)
        }
        this.editRow = this.enrichMembershipPreviewRow(row, detail, payload)
        this.editGroups = this.buildEditGroupsFromPayload(this.payloadForEditJson(payload))
      } catch (e) {
        this.errorMsg = e.message || '读取详情失败'
        this.editVisible = false
      } finally {
        this.editLoading = false
      }
    },
    closeEditPayload() {
      this.editVisible = false
      this.editRow = null
      this.editGroups = []
      this.editLoading = false
      this.editSaving = false
      this.editApplyDutyLocked = false
      this.editMemberMasterLevel = ''
    },
    async saveEditPayload() {
      if (!this.editRow || !this.editRow.id) return
      this.editSaving = true
      this.errorMsg = ''
      try {
        const parsed = this.buildPayloadFromEditGroups()
        if (this.editApplyDutyLocked && this.editMemberMasterLevel) {
          this.writeApplyDutyIntoPayload(parsed, this.editMemberMasterLevel)
        }
        await updateMembershipApplySubmissionPayload(this.editRow.id, parsed)
        this.closeEditPayload()
        await this.reload()
        window.alert('申请表已保存')
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.editSaving = false
      }
    },
    async tryOpenSubmissionFromRoute() {
      const openId = Number(this.$route?.query?.openSubmissionId || 0)
      if (!openId) return
      if (String(this.$route?.query?.openEdit || '') === '1') return
      let row = (this.rows || []).find((r) => Number(r.id) === openId)
      if (!row) {
        row = {
          id: openId,
          applicantName: '',
          applicantUnit: '',
          createdAt: null
        }
      }
      await this.openPreview(row)
    },
    async scrollToHighlightedSubmission() {
      const id = Number(this.$route?.query?.highlightSubmissionId || 0)
      if (!id) return
      this.highlightedSubmissionId = id

      // 筛选项可能把目标行滤掉，定位前先清空
      const hadFilters = !!(
        String(this.reviewFilter || '').trim()
        || String(this.applicantNameQ || '').trim()
        || String(this.applicantUnitQ || '').trim()
      )
      if (hadFilters) {
        this.reviewFilter = ''
        this.applicantNameQ = ''
        this.applicantUnitQ = ''
        this.page = 0
        await this.reload()
      }

      let row = (this.rows || []).find((r) => Number(r.id) === id)
      if (!row) {
        this.page = 0
        await this.reload()
        row = (this.rows || []).find((r) => Number(r.id) === id)
      }
      if (!row) {
        const maxPages = Math.max(0, Number(this.totalPages || 0) - 1)
        while (!row && this.page < maxPages) {
          this.page += 1
          await this.reload()
          row = (this.rows || []).find((r) => Number(r.id) === id)
        }
      }
      // 刚 ensure 出的预录单可能因排序不在当前结果里：拉详情后插到列表顶部以便高亮
      if (!row && this.useMembershipApplyMode) {
        try {
          const detail = await getMembershipApplySubmissionDetail(id)
          if (detail && Number(detail.id || id) > 0) {
            const injected = {
              ...detail,
              id: Number(detail.id || id),
            }
            this.rows = [injected, ...(this.rows || []).filter((r) => Number(r.id) !== id)]
            row = injected
          }
        } catch {
          /* ignore */
        }
      }

      await this.removeHighlightFromRoute()
      if (!row) return
      await this.$nextTick()
      this.$nextTick(() => {
        const ref = this.$refs[`submissionRow-${id}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
      })
    },
    onPageSurfaceClick(e) {
      if (!this.highlightedSubmissionId) return
      const target = e && e.target
      if (!target || typeof target.closest !== 'function') {
        this.clearSubmissionHighlight()
        return
      }
      if (target.closest('tr.row-highlight')) return
      this.clearSubmissionHighlight()
    },
    clearSubmissionHighlight() {
      this.highlightedSubmissionId = 0
    },
    async removeHighlightFromRoute() {
      if (!this.$route?.query?.highlightSubmissionId) return
      const q = { ...(this.$route.query || {}) }
      delete q.highlightSubmissionId
      skipRouteQueryWatch = true
      try {
        const resolved = this.$router.resolve({ path: this.$route.path, query: q })
        if (resolved.fullPath !== this.$route.fullPath) {
          await this.$router.replace({ path: this.$route.path, query: q })
        }
      } catch {
        /* ignore */
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async tryOpenEditFromRoute() {
      if (String(this.$route?.query?.openEdit || '') !== '1') return
      const openId = Number(this.$route?.query?.openSubmissionId || 0)
      if (!openId) return
      let row = (this.rows || []).find((r) => Number(r.id) === openId)
      if (!row) {
        row = { id: openId, applicantName: '', applicantUnit: '', createdAt: null }
      }
      await this.openEditPayload(row)
      await this.clearOpenEditFromRoute()
    },
    async clearOpenEditFromRoute() {
      if (!this.$route?.query?.openEdit && !this.$route?.query?.openSubmissionId) return
      const q = { ...(this.$route.query || {}) }
      delete q.openEdit
      delete q.openSubmissionId
      skipRouteQueryWatch = true
      try {
        const resolved = this.$router.resolve({ path: this.$route.path, query: q })
        if (resolved.fullPath !== this.$route.fullPath) {
          await this.$router.replace({ path: this.$route.path, query: q })
        }
      } catch {
        /* ignore */
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    formatValue(v) {
      if (v == null) return '—'
      if (Array.isArray(v)) return v.join('、') || '—'
      if (typeof v === 'object') return JSON.stringify(v)
      const t = String(v).trim()
      return t || '—'
    },
    toFullUrl(url) {
      const raw = String(url || '').trim()
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      if (raw.startsWith('//')) return `https:${raw}`
      if (raw.startsWith('/')) return `${BASE_URL}${raw}`
      return `${BASE_URL}/${raw}`
    },
    openLink(url) {
      const u = this.toFullUrl(url)
      if (!u) return
      window.open(u, '_blank', 'noopener')
    },
    imagePartsFromValue(v) {
      if (Array.isArray(v)) {
        return v.map((x) => String(x == null ? '' : x).trim()).filter(Boolean)
      }
      const s = String(v || '').trim()
      if (!s) return []
      if (s.startsWith('[') && s.endsWith(']')) {
        try {
          const arr = JSON.parse(s)
          if (Array.isArray(arr)) {
            return arr.map((x) => String(x == null ? '' : x).trim()).filter(Boolean)
          }
        } catch {
          // ignore
        }
      }
      if (s.includes(',')) return s.split(',').map((x) => x.trim()).filter(Boolean)
      return [s]
    },
    syncEditFieldMedia(fld) {
      if (!fld) return
      if (fld.viewType === 'images') {
        fld.previewUrls = this.normalizeImageUrls(fld.value)
      } else if (fld.viewType === 'file') {
        const full = this.toFullUrl(fld.value)
        fld.fileUrl = full
        fld.fileName = full ? this.fileNameFromUrl(fld.value) : ''
      }
    },
    pickEditFieldUpload(fld, kind, replaceIndex) {
      if (!fld || fld.uploading) return
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = kind === 'image' ? 'image/*' : '*'
      input.style.display = 'none'
      document.body.appendChild(input)
      input.addEventListener('change', async () => {
        const file = input.files && input.files[0]
        document.body.removeChild(input)
        if (!file) return
        fld.uploading = true
        this.errorMsg = ''
        try {
          const data = kind === 'image' ? await uploadImage(file) : await uploadFile(file)
          const url = String(data?.url || '').trim()
          if (!url) throw new Error('未获取到文件地址')
          if (kind === 'image') {
            const multi = this.isEditMultiImageField(fld)
            const parts = this.imagePartsFromValue(fld.value)
            if (typeof replaceIndex === 'number' && replaceIndex >= 0 && replaceIndex < parts.length) {
              parts[replaceIndex] = url
              fld.value = multi ? parts.join(',') : url
            } else if (multi) {
              fld.value = parts.length ? `${parts.join(',')},${url}` : url
            } else {
              fld.value = url
            }
          } else {
            fld.value = url
          }
          this.syncEditFieldMedia(fld)
        } catch (e) {
          this.errorMsg = e.message || '上传失败'
        } finally {
          fld.uploading = false
        }
      })
      input.click()
    },
    /** 与小程序入会表一致：仅盖章/多图类字段允许多张；企业 Logo、证件照等为单图 */
    isEditMultiImageField(fld) {
      const key = String((fld && fld.fieldKey) || '').trim().toLowerCase()
      const label = String((fld && fld.label) || '').trim()
      if (key === 'sealfiles' || /seal|stamp/.test(key)) return true
      if (/盖章|多图|多张/.test(label)) return true
      return false
    },
    canAddEditImage(fld) {
      if (!fld) return false
      const urls = Array.isArray(fld.previewUrls) ? fld.previewUrls : []
      if (this.isEditMultiImageField(fld)) return urls.length < 9
      return urls.length === 0
    },
    editImageFieldHint(fld) {
      if (this.isEditMultiImageField(fld)) return '可上传多张，点击「+」继续添加；点击已有图片可更换'
      return '仅支持一张；点击「+」上传，点击已有图片可更换'
    },
    isImageField(field) {
      return String(field?.fieldType || '').toUpperCase() === 'IMAGE'
    },
    looksLikeFileUrl(text) {
      const t = String(text || '').toLowerCase()
      return /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|7z)(\?.*)?$/.test(t)
    },
    fileNameFromUrl(url) {
      const u = String(url || '').split('?')[0]
      const seg = u.split('/').pop() || ''
      return decodeURIComponent(seg) || '附件'
    },
    normalizeImageUrls(v) {
      if (Array.isArray(v)) return v.map((x) => this.toFullUrl(x)).filter(Boolean)
      const s = String(v || '').trim()
      if (!s) return []
      if (s.startsWith('[') && s.endsWith(']')) {
        try {
          const arr = JSON.parse(s)
          if (Array.isArray(arr)) return arr.map((x) => this.toFullUrl(x)).filter(Boolean)
        } catch {
          // ignore
        }
      }
      if (s.includes(',')) return s.split(',').map((x) => this.toFullUrl(x)).filter(Boolean)
      return [this.toFullUrl(s)].filter(Boolean)
    },
    buildFullFormPreview(payload) {
      const groups = Array.isArray(this.groupDefs) ? [...this.groupDefs] : []
      groups.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
      const groupNameById = {}
      groups.forEach((g) => { groupNameById[String(g.id)] = g.groupName || '其他信息' })
      const fields = Array.isArray(this.fieldDefs) ? [...this.fieldDefs] : []
      fields.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
      const bucket = {}
      fields.forEach((f) => {
        const gid = f.groupId != null ? String(f.groupId) : '__other__'
        const gname = groupNameById[gid] || '其他信息'
        if (!bucket[gname]) bucket[gname] = []
        const keyCandidates = [
          String(f.fieldKey || '').trim(),
          `field_${f.id}`,
          `fieldId:${f.id}`,
          `field_id_${f.id}`,
        ].filter(Boolean)
        let raw = null
        for (let i = 0; i < keyCandidates.length; i += 1) {
          const k = keyCandidates[i]
          if (payload && Object.prototype.hasOwnProperty.call(payload, k)) {
            raw = payload[k]
            break
          }
        }
        const item = {
          key: f.label || f.fieldKey,
          value: this.formatValue(raw),
          viewType: 'text',
          urls: [],
          fileUrl: '',
          fileName: ''
        }
        if (this.isImageField(f)) {
          const urls = this.normalizeImageUrls(raw)
          if (urls.length) {
            item.viewType = 'images'
            item.urls = urls
          }
        } else {
          const txt = String(raw == null ? '' : raw).trim()
          const full = this.toFullUrl(txt)
          if (full && this.looksLikeFileUrl(full)) {
            item.viewType = 'file'
            item.fileUrl = full
            item.fileName = this.fileNameFromUrl(full)
          }
        }
        bucket[gname].push(item)
      })
      const out = []
      Object.keys(bucket).forEach((g) => {
        out.push({ key: `【${g}】`, value: '' })
        out.push(...bucket[g])
      })
      if (!out.length) {
        return Object.keys(payload || {})
          .filter((k) => k !== '__audit' && k !== '__flow')
          .map((k) => ({ key: k, value: this.formatValue(payload[k]) }))
      }
      return out
    },
    goBack() {
      const returnTo = String(this.$route?.query?.returnTo || '').trim()
      if (returnTo.startsWith('/')) {
        this.$router.push(returnTo).catch(() => {})
        return
      }
      if (window.history.length > 1) {
        this.$router.back()
        return
      }
      this.$router.push('/dashboard').catch(() => {})
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      async handler() {
        if (skipRouteQueryWatch) return
        this.applyFiltersFromRoute()
        this.page = 0
        await this.reload()
        await this.tryOpenSubmissionFromRoute()
        await this.tryOpenEditFromRoute()
        await this.scrollToHighlightedSubmission()
      },
    },
    formId(n, o) {
      if (!this.useMembershipApplyMode && n !== o) {
        this.page = 0
        this.reload()
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  max-width: 100%;
  box-sizing: border-box;
}
.page-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}
.back-btn:hover {
  transform: translateX(-1px);
}
.back-btn-icon {
  width: 32px;
  height: 32px;
  display: block;
}
h2 {
  margin: 0;
  font-size: 18px;
  color: #101828;
}
.tip {
  background: #f5f8ff;
  border: 1px solid #dae5ff;
  color: #2a4b8d;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  line-height: 1.55;
  font-size: 13px;
}
.error {
  color: #d92d20;
  font-size: 13px;
  margin: 0 0 8px;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 10px;
}
.filters-panel {
  margin-bottom: 8px;
}
.filters-break {
  flex-basis: 100%;
  width: 0;
  height: 0;
  overflow: hidden;
}
.filters-hint {
  margin: 0 0 12px;
  color: #667085;
  font-size: 12px;
  line-height: 1.45;
}
.flt {
  display: flex;
  align-items: center;
}
.flt-range {
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.flt-sep {
  color: #667085;
  font-size: 13px;
  flex-shrink: 0;
}
.flt-select {
  width: 170px;
}
.flt-select--status {
  width: 160px;
}
.flt-select--wide {
  width: 280px;
}
.flt-select--template {
  width: 260px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 160px;
  border-radius: 8px;
}
:deep(.filters .flt-select--wide .fs-btn) {
  min-width: 280px;
}
:deep(.filters .flt-select--template .fs-btn) {
  min-width: 260px;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 8px;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  background: #fff;
}
.submission-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.submission-table th,
.submission-table td {
  border-bottom: 1px solid #edf0f5;
  padding: 10px 12px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
  background: #fff;
}
.submission-table thead th {
  background: #f8fafc;
  color: #344054;
  font-weight: 600;
  white-space: nowrap;
}
.col-check {
  width: 44px;
}
.col-seq {
  width: 56px;
  color: #667085;
}
.col-name {
  min-width: 108px;
  white-space: nowrap;
}
.col-unit {
  min-width: 160px;
  max-width: 220px;
}
.cell-ellipsis {
  display: inline-block;
  max-width: 196px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.col-flow {
  min-width: 132px;
}
.col-audit {
  min-width: 148px;
  white-space: normal;
  color: #475467;
}
.col-audit > div {
  line-height: 1.45;
}
.col-time {
  min-width: 168px;
  white-space: nowrap;
}
.col-sticky-right {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -6px 0 10px rgba(15, 23, 42, 0.06);
}
.submission-table thead .col-sticky-right {
  z-index: 3;
  background: #f8fafc;
}
.col-ops {
  min-width: 320px;
  white-space: normal;
}
.col-ops-grid {
  --op-btn-w: 152px;
  display: grid;
  grid-template-columns: repeat(2, var(--op-btn-w));
  gap: 8px;
  width: calc(var(--op-btn-w) * 2 + 8px);
  margin: 0 auto;
}
.op-btn {
  width: var(--op-btn-w);
  min-height: 32px;
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.25;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: normal;
  box-sizing: border-box;
  text-align: center;
}
.op-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.op-btn--view,
.op-btn--export {
  background: #fff;
  color: #26457d;
  border-color: #bfc9db;
}
.op-btn--edit {
  background: #eef4ff;
  color: #1d4ed8;
  border-color: #c7d7fe;
}
.op-btn--primary {
  background: #2f6af6;
  color: #fff;
  border-color: #2f6af6;
}
.op-btn--fee {
  background: #fff7e6;
  color: #b54708;
  border-color: #fedf89;
}
.op-btn--danger {
  background: #fff1f0;
  color: #d92d20;
  border-color: #ffc8c2;
}
.audit-note {
  color: #667085;
  margin-top: 2px;
}
.muted {
  color: #98a2b3;
  font-size: 13px;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
}
.btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-delete {
  border-color: #ffc8c2;
  background: #fff1f0;
  color: #d92d20;
}
.filter-actions .btn + .btn {
  margin-left: 0;
}
.flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  font-size: 13px;
}
.flt-input--wide {
  width: 280px;
}
.flt-input--date {
  width: 150px;
  min-width: 150px;
  flex: 0 0 150px;
}
.filter-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.filter-action-btn {
  min-width: 108px;
  height: 40px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  box-sizing: border-box;
  white-space: nowrap;
}
.btn-cancel {
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #344054;
}
.pager {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.page-info {
  font-size: 13px;
  color: #475467;
}
.pill {
  display: inline-block;
  min-width: 58px;
  text-align: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-red {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.pill-gray {
  color: #475467;
  background: #f2f4f7;
  border: 1px solid #eaecf0;
}
.flow-sub-hint {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #909399;
}
.row-highlight td {
  background: #fffbeb !important;
  box-shadow: inset 0 0 0 1px #f59e0b;
}
.mask--stack {
  z-index: 1300;
}
.dialog {
  width: min(760px, 92vw);
  max-height: 86vh;
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  overflow: auto;
}
.dialog--shell {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  padding: 0;
}
.dialog-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px;
}
.dialog-foot {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid #edf0f5;
  background: #fff;
}
.dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 14px 14px 10px;
  border-bottom: 1px solid #edf0f5;
  margin-bottom: 0;
}
.dialog-preview,
.dialog-edit {
  width: min(92vw, 920px);
  max-width: 920px;
}
.dialog h3 {
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}
.dialog-secretary {
  width: min(420px, 92vw);
  max-height: 90vh;
}
.dialog-secretary .dialog-body {
  padding-top: 12px;
}
.dialog-secretary .dialog-hint:first-child {
  margin-top: 0;
}
.dialog-hint {
  font-size: 12px;
  color: #475467;
  line-height: 1.55;
  margin: 0 0 12px;
}
.stack-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #344054;
  margin-bottom: 6px;
}
.stack-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  margin-bottom: 10px;
  resize: vertical;
}
.stack-select {
  width: 100%;
  margin-bottom: 14px;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
.preview-meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 12px; margin-bottom: 12px; color: #475467; font-size: 13px; }
.preview-list { border-top: 1px solid #edf0f5; padding-top: 8px; }
.preview-item { display: grid; grid-template-columns: 180px 1fr; gap: 8px; padding: 8px 0; border-bottom: 1px dashed #edf0f5; }
.preview-k { color: #111827; font-weight: 600; word-break: break-all; }
.preview-v { color: #475467; word-break: break-word; white-space: pre-wrap; }
.media-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.media-thumb { width: 96px; height: 96px; object-fit: cover; border-radius: 8px; border: 1px solid #dbe2ef; cursor: pointer; background: #fff; }
.file-name { margin-left: 8px; color: #667085; }
.preview-item--group { background: #f8fafc; border-bottom: 1px solid #e5e7eb; }
.preview-item--group .preview-k { grid-column: 1 / 3; color: #1d4ed8; }
.preview-item--group .preview-v { display: none; }
.reject-note {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #b42318;
  font-size: 13px;
  line-height: 1.55;
}
.preview-reject-wrap {
  grid-column: 1 / -1;
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.preview-reject-label {
  display: block;
  font-size: 12px;
  color: #b42318;
  font-weight: 600;
  margin-bottom: 4px;
}
.preview-reject-text {
  display: block;
  font-size: 13px;
  color: #912018;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.edit-form-scroll {
  flex: 1;
  overflow: auto;
  max-height: 58vh;
  padding-right: 4px;
}
.edit-group { margin-bottom: 16px; }
.edit-group-title {
  margin: 0 0 10px;
  font-size: 14px;
  color: #1d4ed8;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 6px;
}
.edit-field { margin-bottom: 12px; }
.edit-readonly-hint {
  margin: 6px 0 0;
  font-size: 12px;
}
.edit-input:disabled,
.edit-input[readonly] {
  background: #f2f4f7;
  color: #667085;
  cursor: not-allowed;
}
.edit-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.edit-label .req { color: #d92d20; margin-left: 2px; }
.edit-input,
.edit-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}
.edit-textarea { resize: vertical; min-height: 72px; }
.edit-upload-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
}
.edit-upload-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #667085;
}
.upload-box {
  width: 96px;
  height: 96px;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
  box-sizing: border-box;
}
.upload-box:hover {
  border-color: #98b4ff;
  background: #f3f7ff;
  box-shadow: 0 6px 16px rgba(47, 106, 246, 0.1);
}
.upload-box.is-busy {
  pointer-events: none;
  opacity: 0.75;
}
.upload-box--file {
  width: auto;
  min-width: 120px;
  max-width: 280px;
  height: auto;
  min-height: 96px;
  padding: 10px 12px;
}
.upload-file-name {
  font-size: 12px;
  color: #344054;
  word-break: break-all;
  text-align: center;
  line-height: 1.4;
}
.upload-img { width: 100%; height: 100%; object-fit: cover; }
.upload-placeholder {
  color: #98a2b3;
  font-size: 12px;
  padding: 8px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.upload-plus {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid #c8d7ff;
  color: #2f6af6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  background: #fff;
}
.upload-text { font-size: 11px; color: #667085; }
.edit-option-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.edit-option-group--wrap {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
}
.edit-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  transition: border-color .15s ease, background .15s ease;
}
.edit-option--check { min-width: 120px; }
.edit-option--on {
  border-color: #98b4ff;
  background: #f3f7ff;
}
.edit-option input { margin: 0; flex-shrink: 0; }
.preview-actions { border-top: 1px solid #edf0f5; padding-top: 12px; margin-top: 12px; }
.dialog-fee-voucher { width: min(520px, 92vw); max-height: 90vh; }
.dlg-foot-hint { margin: 12px 0 0; font-size: 13px; line-height: 1.5; }
.voucher-img-wrap {
  margin: 12px 0;
  text-align: center;
  max-height: 52vh;
  overflow: auto;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  padding: 10px;
  background: #fafafa;
}
.voucher-img { max-width: 100%; height: auto; border-radius: 8px; }
.voucher-upload-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 160px;
  height: 160px;
  margin: 12px auto;
  border: 2px dashed #c5d0e6;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  color: #5b6b8a;
}
.voucher-upload-add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.voucher-upload-plus {
  font-size: 48px;
  line-height: 1;
  color: #98a8c8;
  font-weight: 300;
}
.voucher-upload-label {
  font-size: 13px;
}
.mock-voucher-hint {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.45;
}
.hidden-file {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}
</style>
